import { forwardRef, type ReactNode } from 'react';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

export interface LoadingButtonProps extends ButtonProps {
    /** The action is in flight: show a spinner, block clicks, keep the button's colour. */
    loading?: boolean;
    /** Label while loading, e.g. "Creating…". Defaults to the normal label. */
    loadingLabel?: ReactNode;
}

/**
 * A Button for actions that wait on the server. While `loading`, a spinner replaces the start icon
 * (or leads the label), the label can change, and the button can't be pressed again. Unlike a plain
 * disabled button it keeps its colour, so it reads as "working", not "unavailable".
 */
export const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(function LoadingButton(
    { loading = false, loadingLabel, startIcon, disabled, children, sx, ...rest },
    ref,
) {
    const spinner = <CircularProgress size={16} thickness={5} color="inherit" aria-hidden />;
    return (
        <Button
            ref={ref}
            {...rest}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            startIcon={loading ? spinner : startIcon}
            sx={[
                loading && {
                    // Stay in colour while working; MUI's disabled style would grey it out
                    '&.Mui-disabled': { color: 'inherit', opacity: 0.9 },
                    '&.MuiButton-contained.Mui-disabled': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                    '&.MuiButton-containedSecondary.Mui-disabled': { backgroundColor: 'secondary.main', color: 'secondary.contrastText' },
                    '&.MuiButton-containedError.Mui-disabled': { backgroundColor: 'error.main', color: 'error.contrastText' },
                    '&.MuiButton-containedWarning.Mui-disabled': { backgroundColor: 'warning.main', color: 'warning.contrastText' },
                    cursor: 'progress',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {loading && loadingLabel !== undefined ? loadingLabel : children}
        </Button>
    );
});
